import { UserModel } from '../models/user-model';
import { UserRoleModel } from '../models/user-role-model';

const FULL_USER_ROLES_CACHE_KEY = 'bajcoFullUserRolesCache';

export interface CachedFullUserRoles {
  userId: string;
  userRoles: UserRoleModel[];
}

/** Unique roles by roleId (first wins). */
export function uniqueUserRolesById(
  roles: UserRoleModel[] | null | undefined
): UserRoleModel[] {
  if (!roles?.length) {
    return [];
  }
  return Array.from(new Map(roles.map((role) => [role.roleId, role])).values());
}

/**
 * `/my` is called with the selected Role header and often returns only that role.
 * Merge with a previously cached full role list so Switch Role keeps all assigned roles.
 */
export function mergeUserRolesPreservingAll(
  cachedRoles: UserRoleModel[] | null | undefined,
  incomingRoles: UserRoleModel[] | null | undefined
): UserRoleModel[] {
  const map = new Map<string, UserRoleModel>();
  for (const role of cachedRoles ?? []) {
    if (role?.roleId) {
      map.set(role.roleId, role);
    }
  }
  for (const role of incomingRoles ?? []) {
    if (role?.roleId) {
      map.set(role.roleId, role);
    }
  }
  return Array.from(map.values());
}

export function withPreservedUserRoles(
  incomingUser: UserModel,
  cachedUser: UserModel | null | undefined
): UserModel {
  return {
    ...incomingUser,
    userRoles: mergeUserRolesPreservingAll(
      cachedUser?.userRoles,
      incomingUser?.userRoles
    ),
  };
}

/** Persist full Switch Role list across tab/browser restarts (sessionStorage does not). */
export function persistFullUserRolesCache(
  user: UserModel | null | undefined
): void {
  if (typeof localStorage === 'undefined' || !user?.id || !user.userRoles?.length) {
    return;
  }
  const payload: CachedFullUserRoles = {
    userId: user.id,
    userRoles: uniqueUserRolesById(user.userRoles),
  };
  try {
    localStorage.setItem(FULL_USER_ROLES_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Quota or privacy mode — Switch Role may fall back to session-only cache.
  }
}

export function readFullUserRolesCache(
  userId: string | null | undefined
): UserRoleModel[] | null {
  if (typeof localStorage === 'undefined' || !userId) {
    return null;
  }
  try {
    const raw = localStorage.getItem(FULL_USER_ROLES_CACHE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as CachedFullUserRoles;
    if (parsed?.userId !== userId || !parsed.userRoles?.length) {
      return null;
    }
    return uniqueUserRolesById(parsed.userRoles);
  } catch {
    return null;
  }
}

export function clearFullUserRolesCache(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.removeItem(FULL_USER_ROLES_CACHE_KEY);
}

/** Memory → session → localStorage fallback for merged Switch Role list. */
export function resolveCachedFullUserForMerge(
  incomingUser: UserModel,
  memoryCached: UserModel | null | undefined,
  sessionCached: UserModel | null | undefined
): UserModel | null {
  if (memoryCached?.userRoles?.length) {
    return memoryCached;
  }

  if (
    sessionCached?.userRoles?.length &&
    sessionCached.id === incomingUser.id
  ) {
    return sessionCached;
  }

  const localRoles = readFullUserRolesCache(incomingUser.id);
  if (localRoles?.length) {
    return { ...incomingUser, userRoles: localRoles };
  }

  return null;
}
